import { Delete } from '@mui/icons-material';
import { CircularProgress, IconButton } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { CSSProperties, useEffect } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Meetingroom, Slot } from '../../../../../../../interfaces/interfaces';
import VyTooltip from '../../../../../../common/VyTooltip';
import NewSlotGroup from './newSlotGroup';
import PresentationCard from './SinglePresentationCard';

interface SlotGroup {
  key: string;
  slots: Slot[];
  datetime_start: string;
  datetime_end: string;
}

interface PresentationsGroupedViewInterafce {
  scrollStyle?: CSSProperties;
  onDragEnd: (result: any) => Promise<void>;
  unassignedSlots: Slot[];
  unassignedSlotsDiv: React.RefObject<HTMLDivElement>;
  isLoadingUnassignedPresentations: boolean;
  isLoadingSlots: boolean;
  onSlotGroupCreate: (
    time: {
      datetime_start: string;
      datetime_end: string;
    },
    withFetch?: boolean | undefined
  ) => void;
  slotGroups: SlotGroup[];
  markedSlots: SlotGroup[];
  makeGroupName(datetime_start: string, datetime_end: string, forDisplay?: boolean): string;
  onSlotGroupDelete: (key: string) => void;
  fetch: (onlyUnassigned?: boolean) => void;
  onEditPresentation: (meetingroom: Meetingroom) => void;
}

const PresentationsGroupedView = ({
  scrollStyle,
  onDragEnd,
  onEditPresentation,
  unassignedSlots,
  unassignedSlotsDiv,
  isLoadingUnassignedPresentations,
  isLoadingSlots,
  onSlotGroupCreate,
  slotGroups,
  markedSlots,
  makeGroupName,
  onSlotGroupDelete,
  fetch,
}: PresentationsGroupedViewInterafce) => {
  const { t } = useTranslation();

  useEffect(() => {
    fetch();
  }, []);

  return (
    <div className="relative">
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="sticky top-10 z-10">
          <div
            ref={unassignedSlotsDiv}
            className="overflow-auto bg-white"
            style={{
              boxShadow:
                scrollStyle?.position !== 'sticky'
                  ? ' 0 0 0 rgba(0, 0, 0, 0.25), 0 0 0 rgba(0, 0, 0, 0.22)'
                  : ' 0 7px 14px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22)',
              minHeight: '200px',
              transition: 'all .3s cubic-bezier(0.25, 0.8, 0.25, 1)',
            }}>
            <div key={'unassigned-slots'} className="flex p-3 mt-8" style={{ zIndex: 3 }}>
              <div className="flex flex-col justify-center items-center min-w-[130px] mr-8 text-[#8f8f8f]">
                <p>{t('account:slots.unassigned-slots')}</p>
              </div>
              <Droppable droppableId={'unassigned-slots'} direction="horizontal">
                {(provided, snapshot) => (
                  <div className="w-full" ref={provided.innerRef} {...provided.droppableProps}>
                    <div className="flex space-x-4">
                      {isLoadingUnassignedPresentations && <CircularProgress />}
                      {unassignedSlots.slice().map((el, index) => (
                        <Draggable draggableId={el._id} key={el._id} index={index}>
                          {(provided, snapshot) => (
                            <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                              <PresentationCard
                                slot={el}
                                isUnassigned
                                onRefresh={() => fetch(true)}
                                onRemove={() => fetch(true)}
                                onEditPresentation={onEditPresentation}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                    </div>
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        </div>
        {isLoadingSlots && (
          <div className="m-3">
            <CircularProgress />
          </div>
        )}
        {slotGroups
          .slice()
          .sort((a, b) => parseInt(a.datetime_start) - parseInt(b.datetime_start))
          .map((el, i) => (
            <div className="overflow-auto my-4" key={el.key}>
              {markedSlots?.map((m) => m?.key).includes(el.key) && (
                <div className="flex flex-row justify-start items-center py-2 w-full">
                  <b>{format(new Date(el.datetime_start), 'dd.MM.yyyy')}</b>
                  <hr style={{ height: '1px', background: 'black', width: '100%', marginLeft: '5px' }} />
                </div>
              )}
              <Droppable droppableId={el.key} direction="horizontal">
                {(provided, snapshot) => (
                  <div key={el.key} className="flex">
                    <div className="flex justify-start items-center min-w-[130px] mr-8 text-[#8f8f8f]">
                      <div
                        className="flex flex-col justify-center items-center"
                        dangerouslySetInnerHTML={{ __html: makeGroupName(el.datetime_start, el.datetime_end, true) }}
                      />

                      {el.slots.length === 0 && (
                        <VyTooltip title={t('account:presentation.delete-empty-section')}>
                          <IconButton onClick={() => onSlotGroupDelete(el.key)}>
                            <Delete />
                          </IconButton>
                        </VyTooltip>
                      )}
                    </div>
                    <div className="w-full" ref={provided.innerRef} {...provided.droppableProps}>
                      <div className="flex mb-4 space-x-2">
                        {el.slots.map((slot, index) => (
                          <Draggable draggableId={slot._id} key={slot._id} index={index}>
                            {(provided, snapshot) => (
                              <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                <PresentationCard slot={slot} onRemove={fetch} onRefresh={fetch} onEditPresentation={onEditPresentation} />
                              </div>
                            )}
                          </Draggable>
                        ))}
                      </div>
                      <hr />
                    </div>
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}

        <div className="flex">
          <div className="flex flex-col justify-center items-center min-w-[130px] mr-8 text-[#8f8f8f]">
            <p>{t('slots:new-time')}</p>
          </div>
          <div className="w-full">
            <NewSlotGroup onCreate={onSlotGroupCreate} presentations={unassignedSlots} />
          </div>
        </div>
      </DragDropContext>
    </div>
  );
};
export default PresentationsGroupedView;
